import React from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import useTranslation from 'hooks/use-translation';

import Wrapper from 'components/wrapper';
import MetaTags from 'components/meta-tags';
import GlobalFooter from 'components/global-footer-rebrand';
import MarketingHeader from 'marketing/components/marketing-header';
import { PageContentContainer, H1, P } from 'info/terms';

export default function PrivacyPage() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(`sm`));

  return (
    <Wrapper>
      <MetaTags variant='privacy' />
      <MarketingHeader variant='legal' />
      <PageContentContainer isMobile={isMobile}>
        <H1>{t('privacy.privacy', 'Privacy Policy')}</H1>
        <P>{t('privacy.lastUpdated', 'Last updated June 2, 2021')}</P>
        {/* legal text will never be translated, hence the following line */}
        {/* eslint-disable i18next/no-literal-string */}
        <P>Welcome to Dutchie!</P>
        <P>
          Courier Plus Inc. (d/b/a Dutchie) ("<strong>Dutchie</strong>", "<strong>we</strong>", "<strong>us</strong>",
          or "<strong>our</strong>") connects cannabis brands, collectives, and cooperatives (collectively, "
          <strong>Dispensaries</strong>") with their customers and/or primary caregivers of those customers ("
          <strong>Customers</strong>" and together with Dispensaries, "<strong>you</strong>" or the "
          <strong>Users</strong>"). We understand and appreciate your concerns about privacy and want you to be familiar
          with how we collect, use and disclose Personal Information (defined below). This Privacy Policy explains what
          Personal Information we collect, how we use and share that information, and your choices concerning our
          information practices.
        </P>
        <P>
          This Privacy Policy governs your use of www.dutchie.com and any other Dutchie owned or operated websites
          (including mobile sites) (collectively, the "<strong>Site</strong>"), mobile applications, widgets and/or any
          interactive features other online services controlled by Dutchie that post a link to this Privacy Policy or
          otherwise reference that this Privacy Policy applies to information collected through the site, application or
          other features or services (all of the foregoing collectively referred to as the "<strong>Service</strong>").
        </P>
        <P>
          This Privacy Policy does not apply to our handling of information collected from Customers and other Users
          when they use a Dispensary’s website to interact with the Service, such as by placing an order directly with a
          Dispensary through the Dispensary’s own website. Data and information collected through such activities are
          governed by the Dispensary’s privacy policy as well as Dutchie’s agreement(s) with the Dispensaries. If you
          have questions about a Dispensary’s privacy practices, please reach out the Dispensary directly.
        </P>
        <P>
          Before using the Service or submitting any Personal Information to Dutchie, please review this Privacy Policy
          carefully and <a href='#contact-us'>contact us</a> if you have any questions. By using the Service, you agree
          to the practices and specifically the collection, disclosure, use and retention of Personal Information
          described in this Privacy Policy. If you do not agree to this Privacy Policy, please do not access the Site or
          otherwise use the Service.
        </P>
        <ul>
          <li>
            <P>Personal Information We Collect</P>
          </li>
          <li>
            <P>How We Use Personal Information</P>
          </li>
          <li>
            <P>Sharing And Disclosure Of Personal Information</P>
          </li>
          <li>
            <P>California Privacy Rights</P>
          </li>
          <li>
            <P>Children</P>
          </li>
          <li>
            <P>Links To Other Websites</P>
          </li>
          <li>
            <P>Security</P>
          </li>
          <li>
            <P>Users in Canada</P>
          </li>
          <li>
            <P>Your Choices</P>
          </li>
          <li>
            <P>Changes To The Privacy Policy</P>
          </li>
          <li>
            <P>Contact Us</P>
          </li>
        </ul>
        <P>
          <strong>1. PERSONAL INFORMATION WE COLLECT</strong>
        </P>
        <P>
          When you interact with us through the Service, we collect and combine information and data from you, including
          information that may identify you or otherwise be considered to be "personal information", "personal data" or
          "personally identifiable information" under laws that regulate privacy and data protection ("
          <strong>Personal Information</strong>") as follows:
        </P>
        <P>
          <strong>Personal Information You Provide</strong>: We collect the following categories of Personal Information
          from you when you visit the Site, create a Dutchie account, place an order through the Service, or otherwise
          provide such information to us:
        </P>
        <ul>
          <li>
            <P>
              <strong>Identification Information</strong>: We collect your name, email address, phone number,
              mailing/billing addresses, date of birth, and government identification documents (e.g., your driver’s
              license, state or provincial identification card, or cannabis medical or license card).
            </P>
          </li>
          <li>
            <P>
              <strong>Financial Information</strong>: For our Dispensaries in the United States, our payment
              processor(s) will collect and store the financial information necessary to process your payments for our
              software services, such as your payment card number and authentication details. Please note, however, that
              we store only a tokenized version of such information and do not maintain payment card information on our
              servers. We do not process payments for Customers in the United States. For our Users in Canada, we may
              process payments directly in compliance with Canadian law, and we only store a tokenized and encrypted
              version of such information for so long as reasonably required to comply with regulatory obligations
              and/or your shopping preferences.
            </P>
          </li>
          <li>
            <P>
              <strong>Communication Information</strong>: We may collect information when you contact us with questions
              or concerns and when you voluntarily respond to questionnaires, surveys or requests for market research
              seeking your opinion and feedback. Providing communication information is optional to you.
            </P>
          </li>
          <li>
            <P>
              <strong>Location Information</strong>: Our Service may use location-based services in order to locate you,
              such as using your zip or postal code or IP address, so that we may verify your location, deliver you
              relevant content based on your location, and share Customer location with the pre-registered delivery
              providers and Dispensaries.
            </P>
          </li>
          <li>
            <P>
              <strong>Commercial Information</strong>: We may retain a history of the products you browse and/or
              purchase using the Service.
            </P>
          </li>
          <li>
            <P>
              <strong>Health Information</strong>: If a Customer makes available a state issued cannabis medical card,
              registration document to possess cannabis for medical purposes in Canada, or other documentation, we may
              collect certain health information included within such materials.
            </P>
          </li>
          <li>
            <P>
              <strong>Social Media Information</strong>: We have pages on social media sites like Instagram, Facebook,
              Medium, and Twitter ("<strong>Social Media Pages</strong>"). When you interact with our Social Media
              Pages, we will collect Personal Information that you elect to provide to us, such as your contact details.
              In addition, the companies that host our Social Media Pages may provide us with aggregate information and
              analytics regarding the use of our Social Media Pages. Your posts, comments, replies to and mention of
              Dutchie may be publicly visible, depending on privacy settings. We do not have control over the privacy
              and retention of your Personal Information on Social Media Pages which are governed by the privacy
              policies of the parties that operate the platforms that host the Social Media Pages. We have no
              responsibility or liability for the manner in which the operators of the Social Media Pages collect, use,
              disclose, secure or otherwise treat your personal information.
            </P>
          </li>
        </ul>
        <P>
          <strong>Internet Activity Information</strong>: When you visit, use, and interact with the Service, we may
          receive certain information about your visit, use, or interactions. For example, we may monitor the number of
          people that visit the Service, peak hours of visits, which page(s) are visited, the domains our visitors come
          from (e.g., google.com, yahoo.com, etc.), and which browsers people use to access the Service (e.g., Chrome,
          Firefox, Microsoft Internet Explorer, etc.), broad geographical information, and navigation pattern. In
          particular, the following information is created and automatically logged in our systems:
        </P>
        <ul>
          <li>
            <P>
              <strong>Log Information</strong>: Information that your browser automatically sends whenever you visit the
              Site. Log Information includes your Internet Protocol address, browser type and settings, the date and
              time of your request, and how you interacted with the Site.
            </P>
          </li>
          <li>
            <P>
              <strong>Cookies Information</strong>: Please see the "<a href='#cookies'>Cookies</a>" section below to
              learn more about how we use cookies.
            </P>
          </li>
          <li>
            <P>
              <strong>Device Information</strong>: Includes name of the device, operating system, and browser you are
              using. Information collected may depend on the type of device you use and its settings.
            </P>
          </li>
          <li>
            <P>
              <strong>Usage Information</strong>: We collect information about how you use our Service, such as the
              types of content that you view or engage with, the features you use, the actions you take, and the time,
              frequency, and duration of your activities.
            </P>
          </li>
          <li>
            <P>
              <strong>Email Open/Click Information</strong>: We may use pixels in our email campaigns that allow us to
              collect your email and IP address as well as the date and time you open an email or click on any links in
              the email.
            </P>
          </li>
        </ul>
        <P>
          <strong>Personal Information Collected from Other Sources</strong>: We may receive Personal Information, such
          as contact information and marketing preferences, from other sources, such as Dispensaries, third-party
          partners, and publicly available information.
        </P>
        <P>
          <strong>Derived Information</strong>: We may infer your preferences for certain products and services based on
          the Personal Information we collect about you.
        </P>
        {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid  */}
        <a id='cookies' />
        <P>
          <strong>Cookies</strong>: We use cookies to operate and administer our Site, gather usage data on our Site,
          and improve your experience on it. A "cookie" is a piece of information sent to your browser by a website you
          visit. Cookies can be stored on your computer for different periods of time. Some cookies expire after a
          certain amount of time, or upon logging out (session cookies), others survive after your browser is closed
          until a defined expiration date set in the cookie (as determined by the third party placing it), and help
          recognize your computer when you open your browser and browse the Internet again (persistent cookies). For
          more details on cookies please visit
          <a href='http://www.allaboutcookies.org/' target='_blank' rel='noreferrer'>
            All About Cookies
          </a>
          .
        </P>
        <P>
          On most web browsers, you will find a "help" section on the toolbar. Please refer to this section for
          information on how to receive a notification when you are receiving a new cookie and how to turn cookies off.
        </P>
        <P>
          Please note that if you limit the ability of websites to set cookies, you may be unable to access certain
          parts of the Site and you may not be able to benefit from the full functionality of the Site.
        </P>
        <P>
          Advertising networks may use cookies to collect Personal Information. Most advertising networks offer you a
          way to opt out of targeted advertising. If you would like to find out more information, please visit the
          Network Advertising Initiative’s
          <a targtet='_blank' href='http://www.networkadvertising.org'>
            online resources
          </a>
          and follow the opt-out instructions there.
        </P>
        <P>
          If you access the Site on your mobile device, you may not be able to control tracking technologies through the
          settings.
        </P>
        <P>
          <strong>Analytics</strong>: We use Google Analytics, a web analytics service provided by Google, Inc. Google
          Analytics uses cookies to help us analyze how users use the Site and enhance your experience when you use the
          Site. For more information on how Google uses this information, click
          <a target='_blank' rel='noreferrer' href='http://www.google.com/policies/privacy/partners/'>
            here
          </a>
          .
        </P>
        <P>
          <strong>Online Tracking and Do Not Track Signals</strong>: We and our third party service providers may use
          cookies, pixels, or other tracking technologies to collect information about your browsing activities over
          time and across different websites following your use of the Site and use that information to send targeted
          advertisements. Our Site currently does not respond to "Do Not Track" ("DNT") signals and operates as
          described in this Privacy Policy whether or not a DNT signal is received. If we do respond to DNT signals in
          the future, we will update this Privacy Policy to describe how we do so.
        </P>
        <P>
          <strong>2. HOW WE USE PERSONAL INFORMATION </strong>
        </P>
        <P>We may use Personal Information for the following purposes: </P>
        <ul>
          <li>
            <P>To provide, operate, and improve the Service and our business; </P>
          </li>
          <li>
            <P>To respond to your inquiries, comments, feedback, or questions; </P>
          </li>
          <li>
            <P>
              To send administrative information to you, for example, information regarding the Service and changes to
              our terms, conditions, and policies;
            </P>
          </li>
          <li>
            <P>
              To send you information or serve you advertisements regarding products or services that we believe may be
              of interest to you;
            </P>
          </li>
          <li>
            <P>To analyze how you interact with our Service; </P>
          </li>
          <li>
            <P>To develop new products and services;</P>
          </li>
          <li>
            <P>
              To prevent fraud, criminal activity, or misuses of our Service, and to ensure the security of our IT
              systems, architecture, and networks; and
            </P>
          </li>
          <li>
            <P>
              To comply with legal obligations and legal process and to protect our rights, privacy, safety, or
              property, and/or that of our affiliates, you, or other third parties.
            </P>
          </li>
        </ul>
        <P>
          <strong>Research and Development</strong>: We may aggregate Personal Information and use the aggregated and
          de-identified information to analyze the effectiveness of the Service, to improve and add features to the
          Service, gain anonymous insights into product and brand performance on the Service, and for other similar
          purposes. In addition, from time to time, we may analyze the general behavior and characteristics of Users and
          share aggregated information like general user statistics with Dispensaries, brands, and other prospective
          business partners.
        </P>
        <P>
          <strong>Communications and Marketing</strong>: We and the Dispensaries you interact with through the Service
          may use your Personal Information, such as your email address and telephone number, to contact you to provide
          the Service and tell you about products or services we believe may be of interest to you.
        </P>
        <P>
          By providing your telephone number, you acknowledge and agree that we and/or our Dispensaries may contact you,
          including but not limited to for promotional, informational and other purposes, using SMS text messages,
          cellular or telephone calls, and push notifications (if applicable) even if you have opted in to the US or
          Canada National Do Not Call List, any state equivalent Do Not Call List or the internal Do Not Call List of
          any company, and that such messages and calls may be sent using automatic dialing systems or artificial or
          prerecorded voice messages.
        </P>
        <P>If you would like to opt-out of receiving certain communications, see the "Your Choices" section below. </P>
        <P>
          <strong>3. SHARING AND DISCLOSURE OF PERSONAL INFORMATION</strong>
        </P>
        <P>
          In certain circumstances we may share the categories of Personal Information described above without further
          notice to you, unless required by the law, with the following categories of third parties:
        </P>
        <ul>
          <li>
            <P>
              <strong>Dispensaries</strong>: To provide the Service, we share certain Personal Information with
              Dispensaries and their service providers (such as point-of-sale systems) to enable Dispensaries to make
              their products available to Customers and for the Dispensaries’ own uses, including marketing, referrals,
              or promotions (such uses will be governed by a Dispensary’s own privacy policy).
            </P>
          </li>
          <li>
            <P>
              <strong>Co-branded Areas</strong>: Certain areas of the Service may be provided to you in association with
              third parties ("Co-Branded Areas") such as promotional partners. If you elect to register for products
              and/or services in Co-Branded Areas, communicate with such third parties or download their content or
              applications through the Service, your Personal Information will be shared with the relevant third party.
              Further, if you sign-in to a Co-Branded Area with your Dutchie account, your Personal Information may be
              disclosed to the identified third parties for that Co-Branded Area. We are not responsible for such third
              party's data collection or practices and you should look to such third party privacy policies for more
              information.
            </P>
          </li>
          <li>
            <P>
              <strong>Vendors and Service Providers</strong>: To assist us in meeting business operations needs and to
              perform certain services and functions, we may share Personal Information with vendors and service
              providers, including providers of hosting services, cloud services, and other information technology
              services providers, email communication software and email newsletter services, advertising and marketing
              services, payment processors, customer relationship management and customer support services, and
              analytics services. Pursuant to our instructions, these parties will access, process, or store Personal
              Information in the course of performing their duties to us.
            </P>
          </li>
          <li>
            <P>
              <strong>Business Transfers</strong>: If we are involved in a merger, acquisition, financing due diligence,
              reorganization, bankruptcy, receivership, dissolution, sale of all or a portion of our assets, or
              transition of service to another provider (collectively and each a "Transaction"), your Personal
              Information and other information may be shared in the diligence process with counterparties and others
              assisting with a Transaction and transferred to a successor or affiliate as part of or following that
              Transaction along with other assets.
            </P>
          </li>
          <li>
            <P>
              <strong>Legal Requirements</strong>: If required to do so by law or in the good faith belief that such
              action is necessary to (i) comply with a legal obligation, including to meet national security or law
              enforcement requirements, (ii) protect and defend our rights or property, (iii) prevent fraud, (iv) act in
              urgent circumstances to protect the personal safety of users of the Service, or the public, or (v) protect
              against legal liability.
            </P>
          </li>
          <li>
            <P>
              <strong>Affiliates</strong>: We may share Personal Information with our current and future affiliates,
              meaning an entity that controls, is controlled by, or is under common control with Dutchie. Our affiliates
              may use the Personal Information we share in a manner consistent with this Privacy Policy. Affiliates may
              be located outside the United States of America and/or Canada.
            </P>
          </li>
          <li>
            <P>
              <strong>Other Users</strong>: The Service may contain a community forum and message board where Users are
              able to create profiles, writings, reviews, photographs, ideas, videos, audio recordings, computer
              graphics, pictures, data, or other content, including Personal Information (collectively, "User Content").
              The Service may display, reproduce, publish, distribute or otherwise use User Content online or offline in
              any media or format (currently existing or hereafter developed) and may or may not attribute it to you.
              Others may have access to this User Content and may have the ability to share it.
            </P>
          </li>
        </ul>
        <P>
          <strong>4. CALIFORNIA PRIVACY RIGHTS</strong>
        </P>
        <P>
          Where provided for by law and subject to any applicable exceptions, California residents may have the right:
        </P>
        <ul>
          <li>
            <P>
              To know the categories of Personal Information that Dutchie has collected about you, the business purpose
              for collecting your Personal Information, and the categories of sources from which the Personal
              Information was collected;
            </P>
          </li>
          <li>
            <P>To access the specific pieces of Personal Information that Dutchie has collected about you; </P>
          </li>
          <li>
            <P>
              To know whether Dutchie has disclosed your Personal Information for business purposes, the categories of
              Personal Information so disclosed, and the categories of third parties to whom we have disclosed your
              Personal Information;
            </P>
          </li>
          <li>
            <P>To have Dutchie, under certain circumstances, delete your Personal Information; </P>
          </li>
          <li>
            <P>
              To instruct businesses that sell Personal Information to stop doing so – Dutchie, however, does not sell
              Personal Information; and
            </P>
          </li>
          <li>
            <P>To be free from discrimination related to the exercise of these rights. </P>
          </li>
        </ul>
        <P>
          If you would like to exercise any of these rights, you may do so at privacy.dutchie.com or by emailing us
          at&nbsp;
          <a href='mailto:privacy@dutchie.com'>privacy@dutchie.com</a>. After we receive your request, we may request
          additional information from you to verify your identity. Your authorized agent may submit requests in the same
          manner, although we may require the agent to present signed written permission to act on your behalf, and you
          may also be required to independently verify your identity with us and confirm that you have provided the
          agent permission to submit the request.
        </P>
        <P>
          <strong>5. CHILDREN</strong>
        </P>
        <P>
          Our Service is not directed to children who are under the age of 18 or adults under the minimum age of
          possession of cannabis in their jurisdiction. Dutchie does not knowingly collect Personal Information from
          children under the age of 18 or adults under the minimum age of possession of cannabis. If you have reason to
          believe that a child under the age of 18 or adults under the minimum age of possession of cannabis has
          provided Personal Information to Dutchie through the Service please <a href='#contact-us'>contact us</a> and
          we will endeavor to delete that information from our databases.
        </P>
        <P>
          <strong>6. LINKS TO OTHER WEBSITES</strong>
        </P>
        <P>
          The Service may contain links to other websites and services not operated or controlled by Dutchie, including
          Dispensaries and social media services ("<strong>Third Party Services</strong>"). The information that you
          share with Third Party Services will be governed by the specific privacy policies and terms of service of the
          Third Party Services and not by this Privacy Policy. By providing these links we do not imply that we endorse
          or have reviewed these Third Party Services. Please contact the Third Party Services directly for information
          on their privacy practices and policies.
        </P>
        <P>
          <strong>7. SECURITY</strong>
        </P>
        <P>
          You use the Service at your own risk. We implement commercially reasonable technical, administrative, and
          organizational measures to protect Personal Information both online and offline from loss, misuse, and
          unauthorized access, disclosure, alteration, or destruction. However, no Internet or e-mail transmission is
          ever fully secure or error free. In particular, e-mail sent to or from us may not be secure. Therefore, you
          should take special care in deciding what information you send to us via the Service or e-mail. Please keep
          this in mind when disclosing any Personal Information to Dutchie via the Internet. In addition, we are not
          responsible for circumvention of any privacy settings or security measures contained on the Service, or Third
          Party Services.
        </P>
        <P>
          <strong>8. USERS IN CANADA</strong>
        </P>
        <P>
          <strong>Storage Outside of Canada</strong>: Your personal information may be used or stored by us or our
          service providers and our corporate affiliates outside of Canada. We require that our service providers and
          our corporate affiliates safeguard your personal information. However, if your personal information is used or
          stored outside of Canada, it will also be subject to the laws of the country in which it is used or stored.
          Your Personal Information is potentially accessible to law enforcement and national security authorities in
          the United States of America. Your information will not be provided to such authorities except in strict
          accordance with the law and if permissible, with prior notice to you. If you have questions about Dutchie’s
          policies and practices with respect to service providers outside of Canada please contact our Privacy Officer.
          Privacy Officer: We have appointed our General Counsel as our Privacy Officer to ensure compliance with this
          Privacy Policy and monitor our compliance with privacy legislation. The contact information for our Privacy
          Officer follows: Lauren Thomas 2728 NW Potts Ct., Suite 100 Bend, OR 97703 privacy@dutchie.com
        </P>
        <P>
          <strong>Deletion</strong>: You may request that we delete your Personal Information at any time at
          privacy.dutchie.com or making a request in writing to our Privacy Officer. We will carry out deletion
          requests, subject to retention to comply with applicable law, within thirty (30) days of receipt and you may
          continue to receive communication from us during this period.
        </P>
        <P>
          <strong>Other International Users</strong>: The Service is not intended for use outside the United States and
          Canada. By using our Service outside of Canada or the United States, you understand and acknowledge that your
          Personal Information will be transferred from your location to our facilities and servers located in the
          United States or Canada, where data protection laws may differ from those in your jurisdiction.
        </P>
        <P>
          <strong>9. YOUR CHOICES</strong>
        </P>
        <P>
          In certain circumstances providing Personal Information is optional. However, if you choose not to provide
          Personal Information that is needed to use some features of our Service, you may be unable to use those
          features. You can also log in to your Dutchie account or <a href='#contact-us'>contact us</a> to request
          updates or corrections to your Personal Information. If you have questions or concerns about other Personal
          Information collected by us, or are unable to use your Dutchie account and would like assistance accessing
          that information, please contact our Privacy Officer at &nbsp;
          <a href='mailto:privacy@dutchie.com'>privacy@dutchie.com</a>.
        </P>
        <P>
          You may opt out of marketing-related communications by following the opt out or unsubscribe instructions
          contained in the marketing communication we send you, or by updating your settings through your account. If
          you unsubscribe from our marketing lists, you will no longer receive marketing communications from us but we
          will continue to contact you to provide the Service, regarding management of your account, other
          administrative matters, and to respond to your requests.
        </P>
        <P>
          <strong>10. CHANGES TO THE PRIVACY POLICY</strong>
        </P>
        <P>
          The Service and our business may change from time to time. As a result we may change this Privacy Policy at
          any time. When we do, we will post an updated version on this page, unless another type of notice is required
          by applicable law. By continuing to use our Service or providing us with Personal Information after we have
          posted an updated Privacy Policy, you consent to the revised Privacy Policy and practices described in it.
        </P>
        <P>
          {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid  */}
          <a id='contact-us' />
          <strong>11. CONTACT US</strong>
        </P>
        <P>
          If you have any questions or complaints about our Privacy Policy or information practices, or would like to
          request a version of this notice in an accessible format, please feel free to contact us at our designated
          request address: <a href='mailto:privacy@dutchie.com'>privacy@dutchie.com</a>.
        </P>
        {/* eslint-enable i18next/no-literal-string */}
      </PageContentContainer>
      <GlobalFooter />
    </Wrapper>
  );
}
